import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import DataTable from 'react-data-table-component';
import Cookies from 'js-cookie';
import Header from '../../common/Header';
import Sidebar from '../../common/Sidebar';
import PageHeader from '../PageHeader';
import Loader from '../../common/Loader';

export default function AIworkflow() {
    document.title = "Digital Employees | AI Workflows";
    const mainHeading = "AI Workflows";
    const heading = `Configurations / ${mainHeading}`;
    const navigate = useNavigate();

    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const access_privileges = JSON.parse(Cookies.get("modulepermissions") || '[]');
    const [customerlist, setcustomerlist] = useState(false);
    const [customeredit, setcustomeredit] = useState(false);

    useEffect(() => {
        access_privileges.forEach((access) => {
            access.sub_module_info.forEach((subaccess) => {
                if (subaccess.name === "Agent Configurations") {
                    if (subaccess.permissions.indexOf(1) !== -1) setcustomerlist(true);
                    if (subaccess.permissions.indexOf(3) !== -1) setcustomeredit(true);
                }
            });
        });
        fetchAIWorkflows(1, perPage);
    }, []);

    const fetchAIWorkflows = async (pageNum = 1, pageSize = 10, search = '') => {
        setIsLoading(true);
        try {
            const token = Cookies.get("admin_user_token");
            const response = await axios.get(
                `/api/admin/ai-workflows/?pagenumber=${pageNum}&perpage=${pageSize}${search ? `&search=${search}` : ''}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                const rows = response.data.data || response.data.results || [];
                const pagination = response.data.pagination || {};
                setListData(rows);
                setCurrentPage(pagination.current_page || pageNum);
                setPerPage(pagination.per_page || pageSize);
                setTotalCount(pagination.total_count || response.data.count || rows.length);
            } else {
                setListData([]);
            }
        } catch (error) {
            console.error('Failed to fetch AI workflows', error);
            setListData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchAIWorkflows(1, perPage, searchQuery);
            setCurrentPage(1);
        }, 600);
        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    const handlePageChange = (page) => {
        fetchAIWorkflows(page, perPage, searchQuery);
    };

    const handlePerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        fetchAIWorkflows(1, newPerPage, searchQuery);
    };

    const columns = [
        {
            name: "S.NO",
            width: "90px",
            selector: (row, index) => ((currentPage - 1) * perPage) + index + 1,
            sortable: true,
        },
        {
            name: "Name",
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2" style={{ maxWidth: "250px" }}>
                    <span
                        className="text-primary fw-bold"
                        title={row.name}
                        style={{
                            maxWidth: "250px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                        }}
                    >
                        {row.name || '--'}
                    </span>
                </div>
            ),
        },
        {
            name: "Description",
            sortable: true,
            grow: 2,
            cell: (row) => (
                <span
                    title={row.description}
                    style={{
                        maxWidth: "320px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                    }}
                >
                    {row.description || '--'}
                </span>
            ),
        },
        {
            name: "Cost",
            sortable: true,
            selector: row => row.pack_price,
            cell: row => (
                <span className="badge bg-primary-subtle text-primary fw-semibold px-2 py-1" style={{ fontSize: '0.78rem' }}>
                    {row.pack_price !== undefined && row.pack_price !== null && row.pack_price !== '' ? `$ ${row.pack_price}` : '--'}
                </span>
            ),
        },
        {
            name: "Duration",
            sortable: true,
            cell: row => (
                row.unit_label ? (
                    <span className="badge rounded-pill bg-info-subtle text-info fw-semibold px-3 py-1" style={{ fontSize: '0.75rem' }}>
                        {row.pack_units} {row.unit_label}
                    </span>
                ) : <span>--</span>
            ),
        },
        {
            name: 'Actions',
            width: '120px',
            center: true,
            cell: row => (
                <div className="action-container">
                    {customeredit && (
                        <span
                            className="action-btn edit-btn"
                            title="Edit"
                            onClick={() => navigate(`/configurations/aiworkflowedit?filename=${encodeURIComponent(row.filename)}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="bi bi-pencil-square"></i>
                        </span>
                    )}
                </div>
            ),
        }
    ];

    const customStyles = {
        table: {
            style: {
                backgroundColor: "#ffffff",
                borderRadius: "8px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#064c77",
                borderBottom: "1px solid #dee2e6",
                minHeight: "52px",
            },
        },
        headCells: {
            style: {
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                textTransform: "uppercase",
            },
        },
        rows: {
            style: {
                minHeight: "56px",
                backgroundColor: "#ffffff",
                "&:hover": {
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                },
            },
        },
        cells: {
            style: {
                fontSize: "14px",
                color: "#495057",
                borderBottom: "1px solid #f1f3f5",
            },
        },
        pagination: {
            style: {
                borderTop: "1px solid #dee2e6",
                padding: "8px",
            },
        },
        noData: {
            style: {
                backgroundColor: "#ffffff",
                color: "#6c757d",
                padding: "24px",
            },
        },
    };

    return (
        <div id='layout-wrapper'>
            <Header />
            <Sidebar />
            <div className='page-content' style={{ backgroundColor: "#ededed" }}>
                <div className="main-content">
                    <PageHeader mainHeading={mainHeading} heading={heading} />
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box">
                                    <div className="page-title-right">
                                        <div className="d-flex justify-content-end align-items-center gap-2">
                                            <div style={{ position: "relative", maxWidth: "250px", width: "100%" }}>
                                                <i
                                                    className="bi bi-search"
                                                    style={{
                                                        position: "absolute",
                                                        left: "10px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        color: "#6c757d",
                                                        fontSize: "14px",
                                                    }}
                                                ></i>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Search workflow..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    style={{ paddingLeft: "32px", borderRadius: '7px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card border-0 shadow-sm">
                            <div className="row">
                                <div className="col-12">
                                    <DataTable
                                        columns={columns}
                                        data={listData}
                                        progressPending={isLoading}
                                        progressComponent={
                                            <div className='loader-overlay text-white'>
                                                <Loader />
                                            </div>
                                        }
                                        customStyles={customStyles}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalCount}
                                        paginationDefaultPage={currentPage}
                                        paginationPerPage={perPage}
                                        onChangeRowsPerPage={handlePerPageChange}
                                        onChangePage={handlePageChange}
                                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                        noDataComponent={
                                            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 w-100">
                                                <div
                                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                                    style={{ width: 72, height: 72, backgroundColor: '#e9f1f7' }}
                                                >
                                                    <i className="bi bi-robot" style={{ fontSize: '34px', color: '#014670' }}></i>
                                                </div>
                                                <h6 className="fw-bold mb-1" style={{ color: '#014670' }}>No AI Workflows yet</h6>
                                                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                                                    There are no AI workflows configured to display here.
                                                </p>
                                            </div>
                                        }
                                        pointerOnHover
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
